import React, { useState, useRef, useCallback, useEffect } from "react";
import { imgUrl } from "../api";
import "./HeroGallery.css";

const TRANSITION_MS = 700;
const AUTOPLAY_MS = 2000;

export default function HeroGallery({ items, onProjectClick, autoplayReady = true }) {
  const [current, setCurrent] = useState(0);
  const [prev, setPrev] = useState(null);
  const [transitioning, setTransitioning] = useState(false);
  const [cursorSide, setCursorSide] = useState("right");
  const [autoplay, setAutoplay] = useState(true);
  const containerRef = useRef(null);
  const videoRefs = useRef([]);
  const transitionTimerRef = useRef(null);
  const autoplayTimerRef = useRef(null);

  // Play only the active video (handles low-battery / autoplay-blocked cases);
  // pause the hidden ones so they don't burn bandwidth and CPU off-screen.
  useEffect(() => {
    videoRefs.current.forEach((video, i) => {
      if (!video) return;
      if (i === current) video.play().catch(() => {});
      else video.pause();
    });
  }, [current]);

  const goTo = useCallback(
    (index) => {
      if (transitioning || items.length <= 1 || index === current) return;

      clearTimeout(transitionTimerRef.current);
      setTransitioning(true);
      setPrev(current);
      setCurrent(index);

      transitionTimerRef.current = setTimeout(() => {
        setPrev(null);
        setTransitioning(false);
      }, TRANSITION_MS);
    },
    [transitioning, items.length, current]
  );

  // Auto-advance to the next slide every AUTOPLAY_MS while autoplay is on.
  // Waits for autoplayReady so the first countdown starts once the gallery
  // is actually visible (e.g. after an intro overlay), not on mount.
  useEffect(() => {
    if (!autoplay || !autoplayReady || items.length <= 1) return undefined;

    autoplayTimerRef.current = setTimeout(() => {
      goTo((current + 1) % items.length);
    }, AUTOPLAY_MS);

    return () => clearTimeout(autoplayTimerRef.current);
  }, [autoplay, autoplayReady, current, items.length, goTo]);

  // Cleanup autoplay timer on unmount
  useEffect(() => () => clearTimeout(autoplayTimerRef.current), []);

  const handleClick = useCallback(
    (e) => {
      if (!containerRef.current || items.length <= 1) return;
      setAutoplay(false);
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      if (x < rect.width / 2) {
        goTo((current - 1 + items.length) % items.length);
      } else {
        goTo((current + 1) % items.length);
      }
    },
    [current, items.length, goTo]
  );

  const handleMouseMove = useCallback((e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setCursorSide(e.clientX - rect.left < rect.width / 2 ? "left" : "right");
  }, []);

  // Cleanup on unmount
  useEffect(() => () => clearTimeout(transitionTimerRef.current), []);

  if (!items || items.length === 0) return null;

  const currentItem = items[current];

  return (
    <div className="hero-gallery-wrapper">
      <div
        className={`hero-gallery hero-gallery--${cursorSide}`}
        ref={containerRef}
        onClick={handleClick}
        onMouseMove={handleMouseMove}
      >
        {items.map((item, i) => {
          const isActive = i === current;
          const isPrev = i === prev;
          const isNext = i === (current + 1) % items.length;
          // incoming slide on top (2), outgoing below (1), rest hidden (0)
          const zIndex = isActive ? 2 : isPrev ? 1 : 0;

          return (
            <div
              key={item._key || i}
              className={`hero-gallery__slide${isActive ? " hero-gallery__slide--active" : ""}`}
              style={{ zIndex }}
            >
              {item.videoUrl ? (
                <video
                  ref={(el) => (videoRefs.current[i] = el)}
                  src={item.videoUrl}
                  autoPlay={isActive}
                  muted
                  loop
                  playsInline
                  // Buffer only what's on screen or about to be
                  preload={isActive || isNext ? "auto" : "none"}
                  webkit-playsinline="true"
                />
              ) : (
                <img
                  src={imgUrl(item.url, 2000)}
                  alt=""
                  decoding="async"
                  fetchPriority={isActive ? "high" : undefined}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="hero-gallery-footer">
        {currentItem?.projectTitle ? (
          <span
            className="hero-gallery-project-title"
            onClick={() => onProjectClick?.(currentItem.projectSlug)}
          >
            {currentItem.projectTitle}<span className="hero-gallery-arrow" aria-hidden="true">
              {/* Drawn rather than the ↗ character, which iOS renders as an emoji */}
              <svg viewBox="0 0 10 10" width="1em" height="1em">
                <path d="M2 8 L8 2 M3.5 2 H8 V6.5" fill="none" stroke="currentColor" strokeWidth="1.1" />
              </svg>
            </span>
          </span>
        ) : (
          <span className="hero-gallery-project-title hero-gallery-project-title--empty" />
        )}

        {items.length > 1 && (
          <div className="hero-gallery-indicator">
            {items.map((item, i) => (
              <div className="hero-gallery-indicator__bar" key={item._key || i}>
                {i < current ? (
                  <div className="hero-gallery-indicator__fill hero-gallery-indicator__fill--done" />
                ) : i === current ? (
                  <div
                    key={autoplay ? `${i}-${current}-play` : `${i}-${current}-paused`}
                    className={`hero-gallery-indicator__fill${
                      autoplay
                        ? " hero-gallery-indicator__fill--active"
                        : " hero-gallery-indicator__fill--done"
                    }`}
                    style={autoplay ? { animationDuration: `${AUTOPLAY_MS}ms` } : undefined}
                  />
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
